"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.TransactionBuilder = exports.ZRX_GAZ_MULTIPLIER = exports.GAS_MULTIPLIER = exports.NULL_ADDRESS = void 0;
const bignumber_js_1 = __importDefault(require("bignumber.js"));
const _ = require("lodash");
const types_1 = require("../types");
const constants_1 = require("../constants");
const swapper_1 = require("./swapper");
const dexs_1 = require("./dexs");
const payload_encoder_1 = require("./payload-encoder");
const AUGUSTUS_V2_ABI = require('../abi/augustus-v2.json');
const AUGUSTUS_V3_ABI = require('../abi/augustus-v3.json');
const AUGUSTUS_V4_ABI = require('../abi/augustus-v4.json');
exports.NULL_ADDRESS = '0x0000000000000000000000000000000000000000';
//Gas units overhead in order to maximize the transaction success rate
exports.GAS_MULTIPLIER = Number(process.env.GAS_MULTIPLIER || 23);
exports.ZRX_GAZ_MULTIPLIER = 70000;
const LENDING_DEXES = ['compound', 'Fulcrum', 'idle', 'aave', 'aave2'];
const UNISWAPV2_FORKS = [
    'uniswapv2',
    'sushiswap',
    'defiswap',
    'linkswap',
    'pancakeswap',
    'pancakeswapv2',
    'apeswap',
    'julswap',
    'streetswap',
    'bakeryswap',
    'quickswap',
    'cometh',
    'dfyn',
    'pantherswap',
];
const UNISWAPV2_ALIAS = ['uniswapv2', 'pancakeswap', 'quickswap'];
/*
BUY SUPPORTED:
aave, balancer, bdai, beth, bzx, chai, compound, idle, kyber, oasis, uniswapv1, uniswapv2, weth, 0x v2, 0x v3

NOT:
bancor, curve
*/
// TODO: add augustus v4 abi
const AugustusAbi = {
    [constants_1.AugustusVersion.v2]: AUGUSTUS_V2_ABI,
    [constants_1.AugustusVersion.v3]: AUGUSTUS_V3_ABI,
    [constants_1.AugustusVersion.v4]: AUGUSTUS_V4_ABI,
};
class TransactionBuilder {
    constructor(network, web3Provider, dexConf, tokens, augustusVersion) {
        this.network = network;
        this.web3Provider = web3Provider;
        this.dexConf = dexConf;
        this.tokens = tokens;
        this.augustusVersion = augustusVersion;
        this.isETHAddress = (address) => address.toLowerCase() === types_1.ETHER_ADDRESS.toLowerCase();
        this.getSimpleSwapValue = (values) => {
            return values.reduce((acc, v) => acc.plus(v), new bignumber_js_1.default(0)).toFixed(0);
        };
        this.getMegaValue = (srcToken, srcAmount, path) => {
            const networkFees = _(path)
                .map(p => p.path)
                .flatten()
                .map(p => p.routes)
                .flatten()
                .reduce((acc, r) => r.networkFee ? acc : acc.plus(r.networkFee), new bignumber_js_1.default('0'));
            const value = this.isETHAddress(srcToken) ? srcAmount : '0';
            return networkFees.plus(value).toFixed(0);
        };
        this.getValue = (srcToken, srcAmount, path) => {
            const networkFees = _(path)
                .map(p => p.routes)
                .flatten()
                .reduce((acc, r) => r.networkFee ? acc : acc.plus(r.networkFee), new bignumber_js_1.default('0'));
            const value = this.isETHAddress(srcToken) ? srcAmount : '0';
            return new bignumber_js_1.default(value).plus(networkFees).toFixed(0);
        };
        this.estimateGas = (swapMethodData, fromUser, value, gasPrice, multiSwapSteps, useReduxToken) => __awaiter(this, void 0, void 0, function* () {
            const gas = yield swapMethodData.estimateGas({
                from: fromUser,
                value,
                data: swapMethodData,
                gasPrice,
            });
            const multiplier = new bignumber_js_1.default(exports.GAS_MULTIPLIER).times(multiSwapSteps);
            const gasOverhead = exports.GAS_MULTIPLIER > 0 ? new bignumber_js_1.default(1).plus(multiplier.dividedBy(100)) : 1;
            // There is some issue with curve on polygon which is making the
            // gas to be underestimated This is just a hack to bump the gas to avoid `out of gas`
            const networkOverhead = this.network === 137 ? 200000 : 0;
            const gasLimit = new bignumber_js_1.default(gas)
                .times(gasOverhead)
                .plus(networkOverhead);
            const gasLimitWithREDUX = useReduxToken
                ? gasLimit.minus(21000).times(1.25).plus(25000).plus(21000)
                : gasLimit;
            return gasLimitWithREDUX.toFixed(0);
        });
        this.getBuyTx = (srcToken, destToken, destAmount, maxAmountIn, priceRoute, userAddress, referrer, referrerIndex, gasPrice, useReduxToken = false, receiver = exports.NULL_ADDRESS, version = constants_1.AugustusVersion.v4) => __awaiter(this, void 0, void 0, function* () {
            const slippageFactor = new bignumber_js_1.default(maxAmountIn).dividedBy(priceRoute.srcAmount);
            const route = yield this.payloadEncoder.getBuyPath(srcToken.address, destToken.address, priceRoute, gasPrice, slippageFactor);
            const params = version === constants_1.AugustusVersion.v4
                ? {
                    fromToken: srcToken.address,
                    toToken: destToken.address,
                    fromAmount: maxAmountIn,
                    toAmount: destAmount,
                    beneficiary: receiver,
                    referrer,
                    useReduxToken,
                    route: route[0].routes,
                }
                : {
                    fromToken: srcToken.address,
                    toToken: destToken.address,
                    fromAmount: maxAmountIn,
                    toAmount: destAmount,
                    expectedAmount: priceRoute.srcAmount,
                    // we keep route structure similar to sell
                    // in lieu of eventually having multihop with buy
                    route: route[0].routes,
                    beneficiary: receiver,
                    referrer,
                };
            const method = this.augustusContract.methods.buy.bind(this.augustusContract);
            const value = this.getValue(srcToken.address, maxAmountIn, route);
            return {
                method,
                value,
                params: { data: params },
            };
        });
        this.getMultiSwapTx = (srcToken, destToken, srcAmount, minAmountOut, priceRoute, userAddress, referrer, referrerIndex, gasPrice, useReduxToken = false, receiver = exports.NULL_ADDRESS, version = constants_1.AugustusVersion.v4) => __awaiter(this, void 0, void 0, function* () {
            const path = yield this.payloadEncoder.getSellPath(srcToken.address, destToken.address, priceRoute, gasPrice);
            const params = version === constants_1.AugustusVersion.v4
                ? {
                    fromToken: srcToken.address,
                    fromAmount: srcAmount,
                    toAmount: minAmountOut,
                    expectedAmount: priceRoute.destAmount,
                    beneficiary: receiver,
                    referrer,
                    useReduxToken,
                    path,
                }
                : {
                    fromToken: srcToken.address,
                    toToken: destToken.address,
                    fromAmount: srcAmount,
                    toAmount: minAmountOut,
                    expectedAmount: priceRoute.destAmount,
                    beneficiary: receiver,
                    referrer,
                    path,
                };
            const method = this.augustusContract.methods.multiSwap.bind(this.augustusContract);
            const value = this.getValue(srcToken.address, srcAmount, path);
            return {
                method,
                value,
                params: { data: params },
            };
        });
        this.getSimpleSwapTx = (srcToken, destToken, srcAmount, toAmount, priceRoute, userAddress, referrer, referrerIndex, gasPrice, useReduxToken = false, beneficiary = exports.NULL_ADDRESS, version) => __awaiter(this, void 0, void 0, function* () {
            const exchangeData = priceRoute.bestRoute.map((br) => {
                const Dex = dexs_1.getDEX(br.exchange);
                if (Dex) {
                    return Dex.getDexData(br, br.exchange);
                }
                else {
                    throw new Error('Unsupported Exchange: ' + br.exchange);
                }
            });
            const { callees, values, calldata, startIndexes, } = yield this.swapper.buildSwap(srcToken.address, destToken.address, srcAmount, toAmount, exchangeData);
            const redux = version === constants_1.AugustusVersion.v4 ? { useReduxToken } : {};
            const params = Object.assign({ fromToken: srcToken.address, toToken: destToken.address, fromAmount: srcAmount, toAmount, expectedAmount: priceRoute.destAmount, callees, exchangeData: calldata, startIndexes,
                values,
                beneficiary,
                referrer }, redux);
            const method = this.augustusContract.methods.simpleSwap.bind(this.augustusContract);
            const value = this.getSimpleSwapValue(values);
            return {
                method,
                value,
                params,
            };
        });
        this.getLegacyMultiswapTx = (srcToken, destToken, srcAmount, minAmountOut, priceRoute, userAddress, referrer, referrerIndex, gasPrice, useReduxToken = false, receiver = exports.NULL_ADDRESS) => __awaiter(this, void 0, void 0, function* () {
            const path = yield this.payloadEncoder.getSellPath(srcToken.address, destToken.address, priceRoute, gasPrice);
            const params = {
                fromToken: srcToken.address,
                toToken: destToken.address,
                fromAmount: srcAmount,
                toAmount: minAmountOut,
                expectedAmount: minAmountOut,
                path,
                mintPrice: '0',
                beneficiary: receiver,
                donationBasisPoints: '0',
                referrer,
            };
            const method = this.augustusContract.methods.multiSwap.bind(this.augustusContract);
            const value = this.getValue(srcToken.address, srcAmount, path);
            return {
                method,
                value,
                params,
            };
        });
        // TODO: Complete buy implementation
        // getSimpleBuyTx = async (
        //   srcToken: Token,
        //   destToken: Token,
        //   srcAmount: PriceString,
        //   minAmountOut: PriceString,
        //   priceRoute: OptimalRatesWithPartnerFees,
        //   userAddress: Address,
        //   referrer: Address,
        //   gasPrice: NumberAsString,
        //   useReduxToken: boolean = false,
        //   receiver: Address = NULL_ADDRESS,
        // ) => {
        // };
        this.getMegaSwapTx = (srcToken, destToken, srcAmount, minAmountOut, priceRoute, userAddress, referrer, referrerIndex, gasPrice, useReduxToken = false, receiver = exports.NULL_ADDRESS) => __awaiter(this, void 0, void 0, function* () {
            const path = yield this.payloadEncoder.getSellMegaPath(srcToken.address, destToken.address, priceRoute, gasPrice);
            const params = {
                fromToken: srcToken.address,
                fromAmount: srcAmount,
                toAmount: minAmountOut,
                expectedAmount: priceRoute.destAmount,
                beneficiary: receiver,
                referrer,
                useReduxToken,
                path,
            };
            const method = this.augustusContract.methods.megaSwap.bind(this.augustusContract);
            const value = this.getMegaValue(srcToken.address, srcAmount, path);
            return {
                method,
                value,
                params: { data: params },
            };
        });
        this.fixUniswapPath = (srcToken, destToken, path) => {
            let _path = _.clone(path);
            _path[0] = this.isETHAddress(srcToken.address) ? types_1.ETHER_ADDRESS : _path[0];
            _path[path.length - 1] = this.isETHAddress(destToken.address)
                ? types_1.ETHER_ADDRESS
                : _path[path.length - 1];
            return _path;
        };
        this.getSwapOnUniswapTx = (srcToken, destToken, srcAmount, minAmountOut, priceRoute, userAddress, referrer, referrerIndex, gasPrice, useReduxToken = false, receiver = exports.NULL_ADDRESS) => __awaiter(this, void 0, void 0, function* () {
            if (!(UNISWAPV2_ALIAS.some(ex => ex.toLowerCase() === priceRoute.bestRoute[0].exchange.toLowerCase()) && priceRoute.bestRoute[0].data.path))
                throw new Error(`TransactionBuilder_getSwapOnUniswapTx: Invalid contractMethod or priceRoute.`);
            const path = this.fixUniswapPath(srcToken, destToken, priceRoute.bestRoute[0].data.path);
            const params = {
                amountIn: srcAmount,
                amountOutMin: minAmountOut,
                path,
                referrer: referrerIndex || 0,
            };
            const method = this.augustusContract.methods.swapOnUniswap.bind(this.augustusContract);
            const value = this.isETHAddress(srcToken.address) ? srcAmount : '0';
            return {
                method,
                value,
                params,
            };
        });
        this.getBuyOnUniswapTx = (srcToken, destToken, dstAmount, maxAmountIn, priceRoute, userAddress, referrer, referrerIndex, gasPrice, useReduxToken = false, receiver = exports.NULL_ADDRESS) => __awaiter(this, void 0, void 0, function* () {
            if (!(UNISWAPV2_ALIAS.some(ex => ex.toLowerCase() === priceRoute.bestRoute[0].exchange.toLowerCase()) && priceRoute.bestRoute[0].data.path))
                throw new Error(`TransactionBuilder_getBuyOnUniswapTx: Invalid contractMethod or priceRoute.`);
            const path = this.fixUniswapPath(srcToken, destToken, priceRoute.bestRoute[0].data.path);
            const params = {
                amountInMax: maxAmountIn,
                amountOut: dstAmount,
                path,
                referrer: referrerIndex || 0,
            };
            const method = this.augustusContract.methods.buyOnUniswap.bind(this.augustusContract);
            const value = this.isETHAddress(srcToken.address) ? maxAmountIn : '0';
            return {
                method,
                value,
                params,
            };
        });
        this.getSwapOnUniswapForkTx = (srcToken, destToken, srcAmount, minAmountOut, priceRoute, userAddress, referrer, referrerIndex, gasPrice, useReduxToken = false, receiver = exports.NULL_ADDRESS) => __awaiter(this, void 0, void 0, function* () {
            if (!(UNISWAPV2_FORKS.includes(priceRoute.bestRoute[0].exchange.toLowerCase()) && priceRoute.bestRoute[0].data.path))
                throw new Error(`TransactionBuilder_getSwapOnUniswapForkTx: Invalid contractMethod or priceRoute.`);
            const path = this.fixUniswapPath(srcToken, destToken, priceRoute.bestRoute[0].data.path);
            const params = {
                factory: priceRoute.bestRoute[0].data.factory,
                initCode: priceRoute.bestRoute[0].data.initCode,
                amountIn: srcAmount,
                amountOutMin: minAmountOut,
                path,
                referrer: referrerIndex || 0,
            };
            const method = this.augustusContract.methods.swapOnUniswapFork.bind(this.augustusContract);
            const value = this.isETHAddress(srcToken.address) ? srcAmount : '0';
            return {
                method,
                value,
                params,
            };
        });
        this.getBuyOnUniswapForkTx = (srcToken, destToken, dstAmount, maxAmountIn, priceRoute, userAddress, referrer, referrerIndex, gasPrice, useReduxToken = false, receiver = exports.NULL_ADDRESS) => __awaiter(this, void 0, void 0, function* () {
            if (!(UNISWAPV2_FORKS.includes(priceRoute.bestRoute[0].exchange.toLowerCase()) && priceRoute.bestRoute[0].data.path))
                throw new Error(`TransactionBuilder_getBuyOnUniswapForkTx: Invalid contractMethod or priceRoute.`);
            const path = this.fixUniswapPath(srcToken, destToken, priceRoute.bestRoute[0].data.path);
            const params = {
                factory: priceRoute.bestRoute[0].data.factory,
                initCode: priceRoute.bestRoute[0].data.initCode,
                amountInMax: maxAmountIn,
                amountOut: dstAmount,
                path,
                referrer: referrerIndex || 0,
            };
            const method = this.augustusContract.methods.buyOnUniswapFork.bind(this.augustusContract);
            const value = this.isETHAddress(srcToken.address) ? maxAmountIn : '0';
            return {
                method,
                value,
                params,
            };
        });
        this.buildTransaction = (srcToken, destToken, srcAmount, destAmount, priceRoute, userAddress, referrer, referrerIndex, gasPrice, receiver, ignoreGas, version, onlyParams = false, useReduxToken = false) => __awaiter(this, void 0, void 0, function* () {
            if (!(version in this.buildersMap &&
                priceRoute.contractMethod.toLowerCase() in this.buildersMap[version]))
                throw new Error('TransactionBuilder_buildTransaction unsupported version or contractMethod');
            const amount = priceRoute.side.toLowerCase() === constants_1.SwapSide.SELL.toLowerCase()
                ? srcAmount
                : destAmount;
            const minMaxAmount = priceRoute.side.toLowerCase() === constants_1.SwapSide.SELL.toLowerCase()
                ? destAmount
                : srcAmount;
            const partialTx = yield this.buildersMap[version][priceRoute.contractMethod.toLowerCase()](srcToken, destToken, amount, minMaxAmount, priceRoute, userAddress, referrer, referrerIndex, gasPrice, useReduxToken, receiver, version);
            if (onlyParams)
                return partialTx.params;
            const method = partialTx.method.apply(null, Object.values(partialTx.params));
            const gas = {};
            if (!ignoreGas) {
                let methodNoRedux = method;
                if (useReduxToken) {
                    const partialTxNoRedux = yield this.buildersMap[version][priceRoute.contractMethod.toLowerCase()](srcToken, destToken, amount, minMaxAmount, priceRoute, userAddress, referrer, referrerIndex, gasPrice, false, // useReduxToken
                    receiver, version);
                    methodNoRedux = partialTxNoRedux.method.apply(null, Object.values(partialTxNoRedux.params));
                }
                gas.gas = yield this.estimateGas(methodNoRedux, userAddress, partialTx.value, gasPrice, this.payloadEncoder.multiSwapSteps(priceRoute), useReduxToken);
            }
            // Fix augustus address for multiple versions!
            return Object.assign(Object.assign({ from: userAddress, to: this.augustusContract._address, chainId: this.network, value: partialTx.value, data: method.encodeABI() }, gas), { gasPrice });
        });
        const augustusAddress = this.dexConf.augustus.exchange;
        this.augustusContract = new this.web3Provider.eth.Contract(AugustusAbi[augustusVersion], augustusAddress);
        this.payloadEncoder = new payload_encoder_1.PayloadEncoder(network, web3Provider, dexConf, tokens, augustusVersion);
        this.swapper = new swapper_1.Swapper(this.network, this.web3Provider, this.augustusContract);
        this.buildersMap = {
            [constants_1.AugustusVersion.v2]: {
                [constants_1.ContractMethod.multiSwap.toLowerCase()]: this.getLegacyMultiswapTx.bind(this),
            },
            [constants_1.AugustusVersion.v3]: {
                [constants_1.ContractMethod.multiSwap.toLowerCase()]: this.getMultiSwapTx.bind(this),
                [constants_1.ContractMethod.simpleSwap.toLowerCase()]: this.getSimpleSwapTx.bind(this),
                [constants_1.ContractMethod.buy.toLowerCase()]: this.getBuyTx.bind(this),
            },
            [constants_1.AugustusVersion.v4]: {
                [constants_1.ContractMethod.multiSwap.toLowerCase()]: this.getMultiSwapTx.bind(this),
                [constants_1.ContractMethod.simpleSwap.toLowerCase()]: this.getSimpleSwapTx.bind(this),
                [constants_1.ContractMethod.buy.toLowerCase()]: this.getBuyTx.bind(this),
                // [ContractMethod.simpleBuy.toLowerCase()]: this.getSimpleBuyTx.bind(
                //   this,
                // ),
                [constants_1.ContractMethod.megaSwap.toLowerCase()]: this.getMegaSwapTx.bind(this),
                [constants_1.ContractMethod.swapOnUniswap.toLowerCase()]: this.getSwapOnUniswapTx.bind(this),
                [constants_1.ContractMethod.buyOnUniswap.toLowerCase()]: this.getBuyOnUniswapTx.bind(this),
                [constants_1.ContractMethod.swapOnUniswapFork.toLowerCase()]: this.getSwapOnUniswapForkTx.bind(this),
                [constants_1.ContractMethod.buyOnUniswapFork.toLowerCase()]: this.getBuyOnUniswapForkTx.bind(this),
            },
        };
    }
}
exports.TransactionBuilder = TransactionBuilder;
//# sourceMappingURL=transaction-builder.js.map